import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';

const SAMPLE_WEBHOOK_PAYLOAD = `
\`\`\`json
{
  "event": "transcriptionComplete",
  "file": {
    "id": "b47e9c12-a5d8-4f3e-9d2b-1c8f3e5d9a0f",
    "folderId": "93ab5c24-e8f7-42d1-b6c9-f4d2e8c7a3b0",
    "summary": "[00:00:00] - Product roadmap review: Q1 goals and feature prioritization\\n[00:02:15] - Mobile app redesign discussion, focus on user experience\\n[00:05:30] - New AI features: Smart summaries and action items extraction\\n[00:08:45] - Performance improvements: 40% faster video processing\\n[00:12:20] - User feedback analysis and feature requests\\n[00:15:00] - Marketing strategy for new AI capabilities\\n[00:18:30] - Team structure and resource allocation\\n[00:22:45] - Budget review and growth projections\\n[00:25:15] - Next steps and action items assignment",
    "url": "https://storage.screenapp.io/meetings/b47e9c12-a5d8-4f3e-9d2b-1c8f3e5d9a0f.mp4",
    "owner": {
      "name": "Sarah Chen",
      "picture": "https://storage.screenapp.io/profiles/sarah-chen.jpg"
    },
    "transcriptUrl": "https://storage.screenapp.io/transcripts/b47e9c12.json",
    "userPromptResponses": {},
    "systemPromptResponses": {
      "SUMMARY_AND_PROMPTS": {
        "promptTitle": "Metadata",
        "responseText": '{
          "speakers": [
            {"SPEAKER_01": "Product Manager"},
            {"SPEAKER_02": "Tech Lead"},
            {"SPEAKER_03": "Design Lead"}
          ],
          "title": "Q1 Product Strategy Meeting",
          "summary": "",
          "transcript_type": "Meeting",
          "specificType": "Strategy Planning",
          "purpose": "Quarterly product planning meeting to align on roadmap, discuss new AI features, and plan resource allocation.",
          "prompts": [
            {
              "title": "AI Feature Impact",
              "prompt": "Analyze the potential impact of the proposed AI features on user workflow and productivity."
            },
            {
              "title": "Resource Planning",
              "prompt": "Outline the resource requirements for implementing the new features while maintaining existing systems."
            }
          ]
        }',
        "createdAt": "2025-02-08T00:30:33.921Z",
        "updatedAt": "2025-02-08T00:30:33.921Z"
      },
      "CHAPTERS": {
        "promptTitle": "Summary",
        "responseText": '{
          "chapters": [
            {
              "title": "Q1 Goals and Roadmap",
              "start": "00:00:00",
              "end": "00:05:30",
              "notes": "Team reviewed Q1 objectives, focusing on AI feature development and mobile app redesign. Key metrics include 30% user growth and 40% improvement in processing speed."
            },
            {
              "title": "AI Feature Development",
              "start": "00:05:30",
              "end": "00:12:20",
              "notes": "Detailed discussion of new AI capabilities including smart summaries, action item extraction, and sentiment analysis. Tech lead presented implementation timeline and resource requirements."
            },
            {
              "title": "User Research Insights",
              "start": "00:12:20",
              "end": "00:18:30",
              "notes": "Analysis of user feedback revealed strong demand for mobile improvements and AI features. Design lead presented user testing results and proposed UX enhancements."
            },
            {
              "title": "Resource Planning",
              "start": "00:18:30",
              "end": "00:25:15",
              "notes": "Team discussed resource allocation, agreed to hire two AI engineers and one mobile developer. Budget approved for Q1 expansion."
            }
          ]
        }',
        "createdAt": "2025-02-08T00:30:33.921Z",
        "updatedAt": "2025-02-08T00:30:33.921Z"
      }
    }
  }
}
\`\`\`
`;

interface LearnMoreProps {
  open: boolean;
  onClose: () => void;
  note?: React.ReactNode;
}

const LearnMore: React.FC<LearnMoreProps> = ({ open, onClose, note }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {t('settings.integrations.webhooks.learnMore.dialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {note}

          <div>
            <p className="text-muted-foreground">
              {t('settings.integrations.webhooks.learnMore.dialog.description')}
            </p>
          </div>

          {/* Types Section */}
          <div>
            <h3 className="text-lg font-semibold border-b pb-2 mb-4">
              {t(
                'settings.integrations.webhooks.learnMore.dialog.sections.types.title',
              )}
            </h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.types.personal.title',
                  )}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.types.personal.description',
                  )}
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.types.team.title',
                  )}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.types.team.description',
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Integrations Section */}
          <div>
            <h3 className="text-lg font-semibold border-b pb-2 mb-4">
              {t(
                'settings.integrations.webhooks.learnMore.dialog.sections.integrations.title',
              )}
            </h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.integrations.communication.title',
                  )}
                </h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.communication.items.0',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.communication.items.1',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.communication.items.2',
                    )}
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.integrations.projectManagement.title',
                  )}
                </h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.projectManagement.items.0',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.projectManagement.items.1',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.projectManagement.items.2',
                    )}
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.integrations.knowledgeManagement.title',
                  )}
                </h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.knowledgeManagement.items.0',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.knowledgeManagement.items.1',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.integrations.knowledgeManagement.items.2',
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Section */}
          <div>
            <h3 className="text-lg font-semibold border-b pb-2 mb-4">
              {t(
                'settings.integrations.webhooks.learnMore.dialog.sections.data.title',
              )}
            </h3>

            <p className="text-muted-foreground mb-4">
              {t(
                'settings.integrations.webhooks.learnMore.dialog.sections.data.description',
              )}
            </p>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.data.meetingIntelligence.title',
                  )}
                </h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.meetingIntelligence.items.0',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.meetingIntelligence.items.1',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.meetingIntelligence.items.2',
                    )}
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.data.aiAnalysis.title',
                  )}
                </h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.aiAnalysis.items.0',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.aiAnalysis.items.1',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.aiAnalysis.items.2',
                    )}
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  {t(
                    'settings.integrations.webhooks.learnMore.dialog.sections.data.completeAssets.title',
                  )}
                </h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.completeAssets.items.0',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.completeAssets.items.1',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.integrations.webhooks.learnMore.dialog.sections.data.completeAssets.items.2',
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Developers Section */}
          <div>
            <h3 className="text-lg font-semibold border-b pb-2 mb-4">
              {t(
                'settings.integrations.webhooks.learnMore.dialog.sections.developers.title',
              )}
            </h3>

            <p className="text-muted-foreground mb-4">
              {t(
                'settings.integrations.webhooks.learnMore.dialog.sections.developers.description',
              )}
            </p>

            <h4 className="font-medium mb-4">
              {t(
                'settings.integrations.webhooks.learnMore.dialog.sections.developers.sampleTitle',
              )}
            </h4>

            <div className="bg-slate-900 text-slate-100 rounded-lg mb-4">
              <pre className="p-4 text-sm whitespace-pre-wrap text-slate-100 break-words">
                <code>
                  {SAMPLE_WEBHOOK_PAYLOAD.replace(/^```json\n/, '').replace(
                    /\n```$/,
                    '',
                  )}
                </code>
              </pre>
            </div>

            <h4 className="font-medium mb-2">
              {t(
                'settings.integrations.webhooks.learnMore.dialog.sections.developers.technicalTitle',
              )}
            </h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              <li>
                {t(
                  'settings.integrations.webhooks.learnMore.dialog.sections.developers.technicalItems.0',
                )}
              </li>
              <li>
                {t(
                  'settings.integrations.webhooks.learnMore.dialog.sections.developers.technicalItems.1',
                )}
              </li>
              <li>
                {t(
                  'settings.integrations.webhooks.learnMore.dialog.sections.developers.technicalItems.2',
                )}
              </li>
              <li>
                {t(
                  'settings.integrations.webhooks.learnMore.dialog.sections.developers.technicalItems.3',
                )}
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>
            {t('settings.integrations.webhooks.learnMore.dialog.buttons.gotIt')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LearnMore;
